<template>
  <div class="container">
    <!-- ===== SEARCH BAR ===== -->
    <div class="search-section">
      <div class="search-box">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="search-icon">
          <circle cx="7" cy="7" r="5" stroke="currentColor" stroke-width="1.5" />
          <path d="M11 11L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        </svg>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Tìm kiếm trong trò chuyện"
          class="search-input"
          @input="onSearchInput"
          @keyup.esc="clearSearch"
        />
        <button v-if="searchQuery" @click="clearSearch" class="clear-btn">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
        </button>
      </div>
      
      <!-- Search Results Count Badge -->
      <div v-if="searchQuery && searchResults.length > 0" class="search-count-badge">
        {{ searchResults.length }} kết quả
      </div>
    </div>

    <!-- ===== SEARCH RESULTS (ZALO STYLE) ===== -->
    <div v-if="showSearchResults" class="search-results-panel">
      <div class="search-results-header">
        <span class="results-title">Tin nhắn</span>
        <button @click="clearSearch" class="close-search-btn" title="Đóng tìm kiếm">✕</button>
      </div>
      <div class="search-results-list">
        <div
          v-for="(result) in searchResults"
          :key="result.id"
          class="search-result-item"
          @click="jumpToMessage(result.id)"
          :class="{ 'active': highlightedMessageId === result.id }"
        >
          <div class="result-avatar">
            <img
              v-if="result.senderType === 'user' && result.avatarUrl"
              :src="result.avatarUrl"
              class="avatar"
              @error="onAvatarError"
            />
            <div v-else :class="['avatar-placeholder', result.senderType === 'oa' ? 'oa-avatar' : 'user-avatar']">
              {{ result.senderType === 'oa' ? 'OA' : 'U' }}
            </div>
          </div>
          <div class="result-content">
            <div class="result-header">
              <span class="result-sender">
                {{ result.senderType === 'oa' ? 'DIAB - Sống khỏe cùng đái tháo đường' : result.userName }}
              </span>
              <span class="result-time">{{ formatTimeAgo(result.timestamp) }}</span>
            </div>
            <div class="result-text" v-html="highlightText(result.text, searchQuery)"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== NO SEARCH RESULTS ===== -->
    <div v-if="searchQuery && searchResults.length === 0 && showSearchResults" class="no-results">
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <circle cx="24" cy="24" r="20" stroke="#9ca3af" stroke-width="2" />
        <path d="M24 16v8M24 28h.01" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" />
      </svg>
      <p>Không tìm thấy kết quả cho "{{ searchQuery }}"</p>
      <button @click="clearSearch" class="clear-search-btn-lg">Xóa tìm kiếm</button>
    </div>

    <!-- ===== MESSAGES CONTAINER ===== -->
    <div
      v-show="!showSearchResults"
      class="messages"
      ref="messagesContainer"
      @scroll="onScroll"
    >
      <div
        v-if="!isExpanded && totalMessages > PAGE_SIZE"
        class="load-more"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" class="spin-icon">
          <path d="M8 2v4M8 10v4M2 8h4M10 8h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        </svg>
        Kéo lên để xem tin nhắn cũ hơn
      </div>

      <div
        v-for="(msg, index) in currentMessages"
        :key="msg.id"
        :ref="el => setMessageRef(el, msg.id)"
        :class="[
          'message-group',
          msg.senderType === 'oa' ? 'received' : 'sent',
          highlightedMessageId === msg.id ? 'highlighted' : ''
        ]"
      >
        <!-- HEADER (ẩn khi liên tiếp) -->
        <div
          class="message-header"
          v-if="!isConsecutive(index)"
        >
          <!-- OA -->
          <template v-if="msg.senderType === 'oa'">
            <div class="avatar-placeholder oa-avatar">OA</div>
          </template>

          <!-- USER -->
          <template v-else>
            <img
              v-if="msg.avatarUrl && msg.avatarUrl.trim() !== ''"
              :src="msg.avatarUrl"
              class="avatar"
              @error="onAvatarError"
            />
            <div v-else class="avatar-placeholder user-avatar">
              U
            </div>
          </template>

          <div class="sender-name">
            {{ msg.senderType === 'oa' ? 'OA' : (msg.userName || 'Người dùng') }}
          </div>
        </div>

        <!-- BODY -->
        <div class="message-body">
          <div class="message-bubble">
            <!-- IMAGE -->
            <img
              v-if="getEventMediaType(msg.eventName) === 'image'"
              :src="msg.vngUrl ?? msg.imageUrl"
              class="message-image"
              @error="onMediaError"
              loading="lazy"
            />

            <!-- VIDEO (small like Zalo) -->
            <video
              v-else-if="getEventMediaType(msg.eventName) === 'video'"
              class="message-video"
              controls
              playsinline
            >
              <source :src="msg.vngUrl" />
            </video>

            <!-- AUDIO -->
            <audio
              v-else-if="getEventMediaType(msg.eventName) === 'audio'"
              class="message-audio"
              controls
            >
              <source :src="msg.vngUrl" />
            </audio>

            <!-- STICKER -->
            <img
              v-else-if="getEventMediaType(msg.eventName) === 'sticker'"
              :src="msg.vngUrl"
              class="message-sticker"
              @error="onMediaError"
            />

            <!-- FILE -->
            <a
              v-else-if="getEventMediaType(msg.eventName) === 'file'"
              :href="msg.vngUrl"
              target="_blank"
              class="message-file"
            >
              📎 {{ msg.fileName || 'Xem file' }}
            </a>

            <!-- TEXT / CAPTION: text gui kem anh, video... nam cung row nen luon phai hien -->
            <div v-if="msg.text" class="message-text">
              {{ msg.text }}
            </div>
          </div>

          <div class="message-time">
            {{ formatTime(msg.timestamp) }}
          </div>
        </div>
      </div>

      <div ref="messagesEnd"></div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ZaloChatBox',
  props: {
    accountId: String,
    preventInitialScroll: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      messageCache: {},
      currentMessages: [],
      selectedConversation: null,
      searchQuery: '',
      searchResults: [],
      highlightedMessageId: null,
      highlightTimeout: null,
      searchDebounceTimeout: null, // ✅ NEW: Debounce for better performance
      messageRefs: {}, // ✅ NEW: Store refs using composition API pattern
      PAGE_SIZE: 20,
      isExpanded: false,
      isRefreshing: false,
      isScrollingToMessage: false,
      isInitialLoad: true, // Track if this is the initial load
    };
  },
  computed: {
    totalMessages() {
      return this.messageCache[this.selectedConversation]?.length || 0;
    },
    // ✅ NEW: Control when to show search results
    showSearchResults() {
      return this.searchQuery.trim() !== '';
    }
  },
  watch: {
    selectedConversation: {
      handler(convId) {
        if (!convId) return;

        const all = this.messageCache[convId] || [];
        this.isExpanded = false;

        this.currentMessages = all.slice(
          Math.max(all.length - this.PAGE_SIZE, 0)
        );

        this.emitConversationImages();

        // Only scroll to bottom if not prevented on initial load
        if (!(this.preventInitialScroll && this.isInitialLoad)) {
          this.$nextTick(() => this.scrollToBottom());
        }

        // After first load, allow scrolling
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
        }
      },
      immediate: true,
    },
  },
  methods: {
    /* ===== NEW: STORE MESSAGE REFS ===== */
    setMessageRef(el, messageId) {
      if (el) {
        this.messageRefs[messageId] = el;
      }
    },

    /* ===== REFRESH CHAT METHOD ===== */
    async refreshChat() {
      if (this.isRefreshing) {
        console.log('⏳ Chat refresh already in progress...');
        return;
      }

      this.isRefreshing = true;
      console.log('🔄 Refreshing chat messages...');

      try {
        // Clear search
        this.clearSearch();
        
        // Reload messages
        await this.loadMessages();
        
        console.log('✅ Chat messages refreshed successfully');
        return true;
      } catch (error) {
        console.error('❌ Failed to refresh chat:', error);
        throw error;
      } finally {
        this.isRefreshing = false;
      }
    },

    /* ===== IMPROVED: SEARCH METHODS WITH DEBOUNCE ===== */
    onSearchInput() {
      // Clear previous debounce timeout
      if (this.searchDebounceTimeout) {
        clearTimeout(this.searchDebounceTimeout);
      }

      if (!this.searchQuery.trim()) {
        this.searchResults = [];
        this.$emit('search-cleared');
        return;
      }
      
      // ✅ Debounce search for better performance (300ms delay)
      this.searchDebounceTimeout = setTimeout(() => {
        this.performSearch();
        this.$emit('search-query-changed', this.searchQuery);
      }, 300);
    },

    performSearch() {
      const query = this.searchQuery.toLowerCase().trim();
      if (!query) {
        this.searchResults = [];
        return;
      }

      const allMessages = this.messageCache[this.selectedConversation] || [];
      
      // ✅ Optimized search with early exit and indexing
      const startTime = performance.now();
      
      this.searchResults = allMessages
        .filter(msg => {
          // Bo qua tin khong co chu (media khong caption)
          if (!msg.text) return false;
          
          // Case-insensitive search
          return msg.text.toLowerCase().includes(query);
        })
        .sort((a, b) => b.timestamp - a.timestamp) // Newest first
        .slice(0, 100); // Limit to 100 results for performance
      
      const endTime = performance.now();
      console.log(`🔍 Found ${this.searchResults.length} results in ${(endTime - startTime).toFixed(2)}ms`);
      
      this.$emit('search-results', this.searchResults);
    },

    clearSearch() {
      this.searchQuery = '';
      this.searchResults = [];
      
      // Clear any pending debounce
      if (this.searchDebounceTimeout) {
        clearTimeout(this.searchDebounceTimeout);
        this.searchDebounceTimeout = null;
      }
      
      // Clear highlight when closing search
      this.clearHighlight();
      
      this.$emit('search-cleared');
    },

    /* ===== FIXED: JUMP TO MESSAGE ===== */
    async jumpToMessage(messageId) {
      console.log('🎯 Jumping to message:', messageId);
      
      // ✅ 1. CLOSE SEARCH MODAL IMMEDIATELY
      this.searchQuery = '';
      this.searchResults = [];
      
      // Prevent auto-scroll during manual jump
      this.isScrollingToMessage = true;
      
      // ✅ 2. Expand all messages to ensure target is in DOM
      const all = this.messageCache[this.selectedConversation] || [];
      this.isExpanded = true;
      this.currentMessages = all;
      
      // ✅ 3. Wait for DOM update (multiple ticks for safety)
      await this.$nextTick();
      await this.$nextTick();
      
      // ✅ 4. Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        const messageEl = this.messageRefs[messageId];
        
        if (messageEl) {
          // Scroll with proper positioning
          messageEl.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
          
          // Highlight the message
          this.setHighlight(messageId);
          
          this.$emit('message-jumped', messageId);
          
          console.log('✅ Scrolled to message:', messageId);
        } else {
          console.warn('⚠️ Message element not found:', messageId);
          console.log('Available refs:', Object.keys(this.messageRefs));
        }
        
        // Reset scroll flag after animation
        setTimeout(() => {
          this.isScrollingToMessage = false;
        }, 1000);
      }, 100);
    },

    /* ===== HIGHLIGHT MANAGEMENT ===== */
    setHighlight(messageId) {
      // Clear previous timeout
      this.clearHighlight();
      
      // Set new highlight
      this.highlightedMessageId = messageId;
      
      // Auto-clear after 3 seconds
      this.highlightTimeout = setTimeout(() => {
        this.highlightedMessageId = null;
        this.highlightTimeout = null;
      }, 3000);
    },

    clearHighlight() {
      if (this.highlightTimeout) {
        clearTimeout(this.highlightTimeout);
        this.highlightTimeout = null;
      }
      this.highlightedMessageId = null;
    },

    /* ===== TEXT HIGHLIGHT IN SEARCH RESULTS ===== */
    highlightText(text, query) {
      if (!query || !text) return text;
      
      // Escape special regex characters
      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      
      return text.replace(regex, '<mark>$1</mark>');
    },

    formatTimeAgo(timestamp) {
      const now = Date.now();
      const msgTime = Number(timestamp);
      const diff = now - msgTime;
      
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);
      
      if (minutes < 1) return 'Vừa xong';
      if (minutes < 60) return `${minutes} phút trước`;
      if (hours < 24) return `${hours} giờ trước`;
      if (days < 7) return `${days} ngày trước`;
      
      return this.formatTime(timestamp);
    },

    /* ===== EVENT TYPE ===== */
    getEventMediaType(eventName = '') {
      if (eventName.includes('_text')) return 'text';
      if (eventName.includes('_image')) return 'image';
      if (eventName.includes('_video')) return 'video';
      if (eventName.includes('_audio')) return 'audio';
      if (eventName.includes('_file')) return 'file';
      if (eventName.includes('_sticker')) return 'sticker';
      return null;
    },

    /* ===== GROUP MESSAGE ===== */
    isConsecutive(index) {
      if (index === 0) return false;
      const prev = this.currentMessages[index - 1];
      const curr = this.currentMessages[index];
      if (!prev || !curr) return false;

      if (curr.senderType === 'oa' && prev.senderType === 'oa') return true;
      if (
        curr.senderType === 'user' &&
        prev.senderType === 'user' &&
        prev.userName === curr.userName
      ) {
        return true;
      }
      return false;
    },

    /* ===== SCROLL ===== */
    onScroll() {
      const el = this.$refs.messagesContainer;
      if (!el || this.isExpanded || this.isScrollingToMessage) return;

      if (el.scrollTop <= 20) {
        this.expandMessages();
      }
    },

    /* ===== EXPAND ===== */
    expandMessages() {
      const all = this.messageCache[this.selectedConversation] || [];
      this.isExpanded = true;
      this.currentMessages = all;

      this.emitConversationImages();

      this.$nextTick(() => {
        this.$refs.messagesContainer.scrollTop = 40;
      });
    },

    /* ===== EMIT ===== */
    emitConversationImages() {
      const images = this.currentMessages
        .filter(
          msg =>
            !msg.eventName?.includes('_sticker') &&
            !msg.eventName?.includes('_text')
        )
        .map(msg => ({
          id: msg.id,
          url: msg.vngUrl ?? msg.imageUrl,
          timestamp: msg.timestamp,
          sender: msg.userName,
          date: this.formatTime(msg.timestamp),
          eventName: msg.eventName,
          vngUrl: msg.vngUrl,
        }));

      this.$emit('media-updated', images);
    },

    /* ===== UTIL ===== */
    formatTime(ts) {
      return new Date(ts).toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    scrollToBottom() {
      if (this.isScrollingToMessage) return;
      this.$refs.messagesEnd?.scrollIntoView({ behavior: 'smooth' });
    },

    /* ===== ERROR HANDLERS ===== */
    onAvatarError(event) {
      event.target.style.display = 'none';
    },

    onMediaError(event) {
      event.target.style.opacity = '0.3';
      event.target.style.filter = 'grayscale(100%)';
    },

    /* ===== LOAD DATA ===== */
    async loadMessages() {
      try {
        const webhookUrl = process.env.VUE_APP_ZALO_WEBHOOK_URL;
        console.log('📡 Fetching messages from:', `${webhookUrl}/ZaloWebHook/ChatGroup/${this.accountId}`);

        const { data } = await axios.get(
          `${webhookUrl}/ZaloWebHook/ChatGroup/${this.accountId}`,
          { params: { export: true } }
        );
        const conv = data?.[0];

        if (!conv) {
          console.warn('⚠️ No conversation data found');
          return;
        }

        const convId = conv.groupName || conv.recipient;

        this.messageCache[convId] = conv.messages.map(msg => ({
          id: msg.id,
          text: msg.message || '',
          imageUrl: msg.imageUrl,
          vngUrl: msg.vngUrl,
          timestamp: Number(msg.timestamp),
          senderType: msg.event_name.includes('oa_') ? 'oa' : 'user',
          userName: msg.userName,
          avatarUrl: msg.avatarUrl,
          fileName: msg.fileName,
          eventName: msg.event_name
        }));

        this.selectedConversation = convId;
        this.$emit('coachIdSelected', conv.coachId);

        if (conv.threadId) {
          this.$emit('thread-id-updated', conv.threadId);
          console.log('🔗 Thread ID:', conv.threadId);
        }

        console.log(`✅ Loaded ${this.messageCache[convId].length} messages`);
      } catch (error) {
        console.error('❌ Failed to load messages:', error);
        console.error('📡 URL attempted:', `${process.env.VUE_APP_ZALO_WEBHOOK_URL}/ZaloWebHook/ChatGroup/${this.accountId}`);
      }
    },
  },

  mounted() {
    this.loadMessages();
  },

  beforeUnmount() {
    // Cleanup timeouts
    this.clearHighlight();
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
  }
};
</script>

<style scoped>
/* ===== LAYOUT ===== */
.container {
  height: 100vh;
  background: #ebecf0;
  display: flex;
  flex-direction: column;
}

/* ===== SEARCH SECTION ===== */
.search-section {
  border-radius: 30px;
  background: #ffffff;
  border-bottom: 1px solid #d1d5db;
  padding: 6px 8px;
  position: relative;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  transition: all 0.2s;
}

.search-box:focus-within {
  background: #ffffff;
  border-color: #01645a;
  box-shadow: 0 0 0 3px rgba(1, 100, 90, 0.1);
}

.search-icon {
  color: #9ca3af;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  background: none;
  outline: none;
  font-size: 14px;
  color: #111827;
}

.search-input::placeholder {
  color: #9ca3af;
}

.clear-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.clear-btn:hover {
  background: #e5e7eb;
  color: #111827;
}

/* Search Count Badge */
.search-count-badge {
  position: absolute;
  top: -8px;
  right: 16px;
  background: #01645a;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(1, 100, 90, 0.3);
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    transform: translateY(-10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* ===== SEARCH RESULTS PANEL ===== */
.search-results-panel {
  flex: 1;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.search-results-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.results-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.close-search-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  transition: all 0.2s;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-search-btn:hover {
  color: #111827;
  background: #e5e7eb;
}

.search-results-list {
  flex: 1;
  overflow-y: auto;
}

.search-result-item {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 1px solid #f3f4f6;
  position: relative;
}

.search-result-item:hover {
  background: #f9fafb;
}

.search-result-item.active {
  background: #fef3c7;
  border-left: 3px solid #f59e0b;
}

.result-avatar {
  flex-shrink: 0;
}

.result-avatar .avatar,
.result-avatar .avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.result-content {
  flex: 1;
  min-width: 0;
}

.result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  gap: 8px;
}

.result-sender {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-time {
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;
}

.result-text {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.result-text :deep(mark) {
  background: #fef3c7;
  color: #92400e;
  font-weight: 600;
  padding: 2px 4px;
  border-radius: 3px;
}

/* ===== NO RESULTS ===== */
.no-results {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px 20px;
  background: #ffffff;
}

.no-results p {
  font-size: 14px;
  color: #9ca3af;
  margin: 0;
  text-align: center;
}

.clear-search-btn-lg {
  padding: 8px 20px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-search-btn-lg:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
}

/* ===== MESSAGES ===== */
.messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  scroll-behavior: smooth;
}

.load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  font-size: 12px;
  color: #9ca3af;
  padding: 8px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 8px;
}

.spin-icon {
  animation: spin 2s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ===== MESSAGE HIGHLIGHT ===== */
.message-group.highlighted {
  animation: highlightPulse 0.6s ease-in-out;
  position: relative;
}

.message-group.highlighted::before {
  content: '';
  position: absolute;
  inset: -8px;
  border: 3px solid #fbbf24;
  border-radius: 16px;
  pointer-events: none;
  animation: borderFade 3s ease forwards;
}

@keyframes highlightPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

@keyframes borderFade {
  0% {
    opacity: 1;
    border-width: 3px;
  }
  100% {
    opacity: 0;
    border-width: 1px;
  }
}

/* ===== GROUP ===== */
.message-group.sent { align-items: flex-end; }
.message-group.received { align-items: flex-start; }

/* ===== HEADER ===== */
.message-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-group.sent .message-header {
  flex-direction: row-reverse;
}

.avatar,
.avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.avatar { 
  object-fit: cover;
  border: 1px solid #e5e7eb;
}

.oa-avatar {
  background: #01645a;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-avatar {
  background: #9ca3af;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sender-name {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}

/* ===== BODY ===== */
.message-body {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.message-group.sent .message-body {
  flex-direction: row-reverse;
}

/* ===== BUBBLE ===== */
.message-bubble {
  padding: 10px 14px;
  border-radius: 14px;
  max-width: 72%;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
}

.message-group.received .message-bubble {
  background: #01645a;
  color: #fff;
}

.message-group.sent .message-bubble {
  background: #ffffff;
  color: #1f2937;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}

/* ===== MEDIA ===== */
.message-image {
  margin-top: 6px;
  width: 180px;        
  max-width: 100%;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.message-image:hover {
  transform: scale(1.02);
}

.message-video {
  margin-top: 8px;
  width: 220px;
  max-width: 100%;
  border-radius: 10px;
  background: #000;
}

.message-audio {
  margin-top: 8px;
  width: 240px;
}

.message-sticker {
  margin-top: 6px;
  max-width: 120px;
}

.message-file {
  margin-top: 6px;
  padding: 8px 12px;
  background: rgba(0,0,0,0.08);
  border-radius: 8px;
  font-size: 13px;
  text-decoration: none;
  color: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
}

.message-file:hover {
  background: rgba(0,0,0,0.12);
}

/* Caption dung duoi media moi can tach dong, text don le thi khong */
.message-bubble > .message-text:not(:first-child) {
  margin-top: 6px;
}

.message-time {
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
}

/* ===== SCROLLBAR ===== */
.messages::-webkit-scrollbar,
.search-results-list::-webkit-scrollbar {
  width: 8px;
}

.messages::-webkit-scrollbar-track,
.search-results-list::-webkit-scrollbar-track {
  background: transparent;
}

.messages::-webkit-scrollbar-thumb,
.search-results-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.messages::-webkit-scrollbar-thumb:hover,
.search-results-list::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .message-bubble {
    max-width: 85%;
  }
  
  .message-image {
    width: 160px;
  }
  
  .message-video {
    width: 200px;
  }
}
</style>